import {
  Box,
  Button,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ChatFlowCaption from 'components/@home/@settings/Tabs/ChatFlowCaption';
import chatFlowFormik from 'components/@home/@settings/Tabs/chatFlowFormik';
import connector from 'components/@home/@settings/Tabs/connector';
import withContext from 'components/@home/@settings/Tabs/withContext';
import FormikAutosuggest from 'components/formik/Autosuggest';
import TextField from 'components/formik/TextField';
import { Field, useFormikContext } from 'formik';
import ArrowBackIcon from 'mdi-react/ArrowBackIcon';
import CloseIcon from 'mdi-react/CloseIcon';
import { arrayOf, func, object, string } from 'prop-types';
import React, { useMemo } from 'react';
import { hasCompanyFeature } from 'utils/companyFeatureFlags';
import { I18n } from 'utils/i18n';

const getStyles = theme => ({
  root: {
    flexGrow: 1,
    display: 'flex',
    backgroundColor: theme.palette.background.paper,
    flexDirection: 'column',
    borderLeft: `solid 1px ${theme.palette.secondary.extraLight}`,
  },
  title: {
    fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
    fontSize: theme.typography.pxToRem(18),
    fontWeight: 'normal',
    fontStyle: 'normal',
    fontStretch: 'normal',
    color: theme.palette.primary.ultraDark,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
  },
  close: {
    color: theme.palette.primary.ultraDark,
  },
  body: {
    flexGrow: 1,
    overflow: 'auto',
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    paddingTop: theme.spacing(3),
  },
  footer: {
    borderTop: `1px solid ${theme.palette.divider}`,
    paddingTop: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(2),
  },
  heading: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: theme.typography.fontWeightBold,
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(2),
  },
});

const ChatFlow = ({ handleSubmit, languages, context, authCompany = null }) => {
  const theme = useTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);
  const { values, setFieldValue } = useFormikContext();
  const hasAgentic = hasCompanyFeature(authCompany?.featureFlags, 'agenticChatFlow');
  const isNew = values._id === 'new';
  const languageSuggestions = languages.map(l => ({
    value: l.shortName,
    label: `${l.name} (${l.shortName.toUpperCase()})`,
  }));
  return (
    <Box component="form" onSubmit={handleSubmit} sx={styles.root}>
      <Box component="header" sx={styles.header}>
        <Box sx={styles.headerLeft}>
          {context.length > 1 && (
            <IconButton
              sx={styles.close}
              edge="start"
              aria-label="close"
              onClick={context.pop}
              size="large"
            >
              <ArrowBackIcon />
            </IconButton>
          )}
          <Typography size="small" sx={styles.title}>
            {I18n.t('ChatFlowSettings.Flow')}
          </Typography>
        </Box>
        <div>
          <IconButton
            sx={styles.close}
            edge="end"
            aria-label="close"
            onClick={context.clear}
            size="large"
          >
            <CloseIcon />
          </IconButton>
        </div>
      </Box>
      <Box component="section" sx={styles.body}>
        <ChatFlowCaption section="Flow" />
        {isNew && hasAgentic && (
          <>
            <Typography sx={styles.heading}>{I18n.t('ChatFlowSettings.Flow type')}</Typography>
            <ToggleButtonGroup
              exclusive
              fullWidth
              value={values.mode || 'pages'}
              onChange={(_, mode) => mode && setFieldValue('mode', mode)}
              sx={{ mb: 2 }}
            >
              <ToggleButton value="pages">{I18n.t('ChatFlowSettings.FlowTypePages')}</ToggleButton>
              <ToggleButton value="agent">{I18n.t('ChatFlowSettings.FlowTypeAgent')}</ToggleButton>
            </ToggleButtonGroup>
          </>
        )}
        <Typography sx={styles.heading}>{I18n.t('ChatFlowSettings.Display name')}</Typography>
        <Field
          component={TextField}
          name="name"
          placeholder={I18n.t('ChatFlowSettings.Display name')}
        />
        <Typography sx={styles.heading}>{I18n.t('ChatFlowSettings.Language')}</Typography>
        <Field
          component={FormikAutosuggest}
          suggestions={languageSuggestions}
          placeholder={I18n.t('ChatFlowSettings.Language')}
          name="language"
        />
      </Box>
      <Box component="footer" sx={styles.footer}>
        <div />
        <Button variant="contained" color="primary" type="submit">
          {I18n.t('ChatFlowSettings.Save')}
        </Button>
      </Box>
    </Box>
  );
};

ChatFlow.propTypes = {
  handleSubmit: func.isRequired,
  languages: arrayOf(string).isRequired,
  context: object.isRequired,
  authCompany: object,
};

export default connector(withContext(chatFlowFormik(ChatFlow)));
